import type { AdminOrderListItem } from "@models/order";
import { formatOrderNumber } from "@utils/formatter";
import { STATUS_META, paymentStatusLabel } from "../_components/status";

// The Orders list's bulk Export (#473): the selected rows, as the list read
// returned them, written as CSV (RFC 4180) for a spreadsheet.

const pad = (value: number): string => String(value).padStart(2, "0");

// The local calendar day, as YYYY-MM-DD: the day the list shows.
const localDay = (date: Date): string =>
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

const dayOf = (value: string | null): string => {
    if (!value) return "";
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? "" : localDay(date);
};

const COLUMNS: Array<[string, (order: AdminOrderListItem) => string]> = [
    [
        "Order #",
        (order) =>
            order.orderNumberFormatted ?? formatOrderNumber(order.orderNumber),
    ],
    ["Customer", (order) => order.organizationName],
    ["Location", (order) => order.locationName],
    ["Status", (order) => STATUS_META[order.status].label],
    // A plain number, so a spreadsheet can add the column up.
    ["Total", (order) => order.total.toFixed(2)],
    ["Created", (order) => dayOf(order.createdAt)],
    ["Last updated", (order) => dayOf(order.lastUpdatedAt)],
    ["Placed by", (order) => order.userName],
    ["Placed by email", (order) => order.userEmail],
    [
        "Payment status",
        (order) =>
            order.paymentStatus === null
                ? ""
                : paymentStatusLabel(order.paymentStatus),
    ],
    ["Ship-to street", (order) => order.shippingStreetAddress],
    ["Ship-to city", (order) => order.shippingCity],
    ["Ship-to state", (order) => order.shippingState],
    ["Ship-to ZIP", (order) => order.shippingZipCode],
    ["Carrier", (order) => order.carrier ?? ""],
    ["Tracking #", (order) => order.trackingNumber ?? ""],
];

// Customer and location names are typed by customers (/apply), and a cell a
// spreadsheet reads as a formula would run when the admin opens the file, so
// such a cell is written as text with a leading apostrophe.
const FORMULA_START = /^[=+\-@\t\r]/;

const cell = (value: string): string => {
    const text = FORMULA_START.test(value) ? `'${value}` : value;
    return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

const BOM = String.fromCharCode(0xfeff);

// A UTF-8 byte-order mark first, so Excel reads the file as UTF-8, and CRLF
// after every line.
export const buildOrdersCsv = (rows: AdminOrderListItem[]): string => {
    const lines = [
        COLUMNS.map(([header]) => cell(header)),
        ...rows.map((order) => COLUMNS.map(([, value]) => cell(value(order)))),
    ];

    return BOM + lines.map((line) => `${line.join(",")}\r\n`).join("");
};

export const ordersCsvFileName = (today: Date): string =>
    `orders-${localDay(today)}.csv`;
